import type { Id, WidgetSpec } from '@hero/schema';
import { BotIcon } from '@hero/web-icons';
import { ClientCardWidget } from './ClientCardWidget';
import { DriverCardWidget } from './DriverCardWidget';
import { LanguagePickerWidget } from './LanguagePickerWidget';
import { OrderCardWidget } from './OrderCardWidget';
import { SavedLocationCardWidget } from './SavedLocationCardWidget';
import { VehicleCardWidget } from './VehicleCardWidget';

/**
 * Single dispatcher for inline chat widgets.
 *
 * Each entity widget is a tappable card that opens the host app's
 * existing drawer via {@link openEntityDrawer}.
 */
export const WidgetRenderer = ({ widget }: { widget: WidgetSpec }) => {
  switch (widget.type) {
    case 'order_card':
      return (
        <OrderCardWidget orderId={widget.orderId} displaySnapshot={widget.displaySnapshot} />
      );
    case 'order_list':
      return <OrderListWidget orderIds={widget.orderIds} title={widget.title} />;
    case 'client_card':
      return (
        <ClientCardWidget clientId={widget.clientId} displaySnapshot={widget.displaySnapshot} />
      );
    case 'driver_card':
      return (
        <DriverCardWidget driverId={widget.driverId} displaySnapshot={widget.displaySnapshot} />
      );
    case 'vehicle_card':
      return (
        <VehicleCardWidget
          vehicleId={widget.vehicleId}
          displaySnapshot={widget.displaySnapshot}
        />
      );
    case 'saved_location_card':
      return (
        <SavedLocationCardWidget
          savedLocationId={widget.savedLocationId}
          displaySnapshot={widget.displaySnapshot}
        />
      );
    case 'confirmation_card':
      return null;
    case 'language_picker':
      return <LanguagePickerWidget options={widget.options} />;
    default:
      return <UnknownWidget type={widget.type} />;
  }
};

const OrderListWidget = ({ orderIds, title }: { orderIds: Id[]; title?: string }) => (
  <div className='flex flex-col gap-2'>
    {title ? (
      <p className='text-xs font-medium text-slate-500'>{title}</p>
    ) : null}
    <div className='flex flex-col gap-2'>
      {orderIds.map(id => (
        <OrderCardWidget key={String(id)} orderId={id} />
      ))}
    </div>
  </div>
);

const UnknownWidget = ({ type }: { type: string }) => (
  <div className='inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500'>
    <BotIcon className='size-3' />
    <span>{type.replace(/_/g, ' ')}</span>
  </div>
);
